import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Phone, MapPin, Calendar, Filter, RefreshCw, Loader,
  CheckCircle, Edit2, MoreHorizontal, X,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import EditJobOrderModal from './EditJobOrderModal';
import CompleteJobOrderModal from './CompleteJobOrderModal';

const T = {
  bg: 'var(--nd-bg)', surface: 'var(--nd-surface)', surf2: 'var(--nd-surface-2)', surf3: 'var(--nd-surface-2)',
  border: 'var(--nd-w08)', bord2: 'var(--nd-w10)',
  text: 'var(--nd-text)', muted: 'var(--nd-tx35)',
  blue: '#3B82F6', teal: '#22D3A5', amber: '#FBBF24', red: '#F87171',
  green: '#34D399', cyan: '#22D3EE', purple: '#A78BFA',
  font: "'DM Sans',sans-serif", mono: "'DM Mono',monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin { to { transform: rotate(360deg); } }
  .jol-input,.jol-select,.jol-date {
    width:100%; background:var(--nd-w05); border:1px solid var(--nd-w10);
    border-radius:9px; color:var(--nd-text); font-size:12px; font-family:'DM Sans',sans-serif;
    outline:none; transition:border-color 0.15s,background 0.15s;
  }
  .jol-input { padding:8px 11px 8px 32px; }
  .jol-select,.jol-date { padding:8px 11px 8px 32px; appearance:none; cursor:pointer; color-scheme:dark; }
  .jol-select option { background:var(--nd-surface-2); color:var(--nd-text); }
  .jol-input::placeholder { color:var(--nd-tx22); }
  .jol-input:focus,.jol-select:focus,.jol-date:focus { border-color:rgba(59,130,246,0.55); background:rgba(59,130,246,0.05); }
  .jol-check-wrap { display:flex; align-items:center; gap:8px; cursor:pointer; padding:8px 12px; border-radius:9px; background:var(--nd-w04); border:1px solid var(--nd-w08); transition:background 0.15s; white-space:nowrap; }
  .jol-check-wrap:hover { background:var(--nd-w08); }
  .jol-checkbox { width:14px; height:14px; accent-color:#3B82F6; cursor:pointer; flex-shrink:0; }
  .jol-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 13px; border-radius:8px; font-size:11px; font-weight:700; font-family:'DM Sans',sans-serif; cursor:pointer; border:1px solid; transition:all 0.15s; white-space:nowrap; }
  .jol-btn-apply   { background:linear-gradient(135deg,#3B82F6,#22D3A5); color:var(--nd-text-strong); border-color:transparent; box-shadow:0 4px 12px rgba(59,130,246,0.22); }
  .jol-btn-apply:hover { opacity:.88; }
  .jol-btn-reset   { background:var(--nd-w05); color:var(--nd-tx45); border-color:var(--nd-w10); }
  .jol-btn-reset:hover { background:var(--nd-w08); color:var(--nd-tx70); }
  .jol-btn-refresh { background:rgba(34,211,165,0.09); color:#22D3A5; border-color:rgba(34,211,165,0.22); }
  .jol-btn-refresh:hover { background:rgba(34,211,165,0.16); border-color:rgba(34,211,165,0.38); }
  .jol-btn-complete { background:rgba(52,211,153,0.10); color:#34D399; border-color:rgba(52,211,153,0.25); padding:5px 9px; border-radius:6px; }
  .jol-btn-complete:hover { background:rgba(52,211,153,0.18); border-color:rgba(52,211,153,0.45); }
  .jol-btn-edit { background:rgba(59,130,246,0.10); color:#60A5FA; border-color:rgba(59,130,246,0.25); padding:5px 9px; border-radius:6px; }
  .jol-btn-edit:hover { background:rgba(59,130,246,0.18); border-color:rgba(59,130,246,0.45); }
  .jol-th { padding:10px 13px; font-size:9px; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; color:var(--nd-tx30); font-family:'DM Sans',sans-serif; border-bottom:1px solid var(--nd-w08); white-space:nowrap; background:var(--nd-surface-2); }
  .jol-tr { border-bottom:1px solid var(--nd-w05); transition:background 0.12s; }
  .jol-tr:last-child { border-bottom:none; }
  .jol-tr:hover td { background:var(--nd-w03) !important; }
  .jol-td { padding:11px 13px; vertical-align:middle; }
  .jol-row-today     td { background:rgba(59,130,246,0.06)  !important; }
  .jol-row-week      td { background:rgba(34,211,238,0.04)  !important; }
  .jol-row-overdue   td { background:rgba(248,113,113,0.05) !important; }
  .jol-row-completed td { background:rgba(52,211,153,0.04)  !important; }
  .jol-page-btn { min-width:30px; height:30px; padding:0 6px; border-radius:7px; border:1px solid var(--nd-w10); background:var(--nd-w04); color:var(--nd-tx45); font-size:11px; font-weight:700; font-family:'DM Mono',monospace; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all 0.15s; }
  .jol-page-btn:hover:not(:disabled) { background:var(--nd-w08); color:var(--nd-text); }
  .jol-page-btn:disabled { opacity:0.3; cursor:not-allowed; }
  .jol-page-btn.active { background:linear-gradient(135deg,#3B82F6,#22D3A5); color:var(--nd-text-strong); border-color:transparent; box-shadow:0 3px 10px rgba(59,130,246,0.3); }
  ::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--nd-w10);border-radius:4px}
`;

const statusCfg = s => ({
  completed:  { color:'#34D399', bg:'rgba(52,211,153,0.12)',  border:'rgba(52,211,153,0.28)'  },
  pending:    { color:'#FBBF24', bg:'rgba(251,191,36,0.12)',  border:'rgba(251,191,36,0.28)'  },
  dispatched: { color:'#22D3EE', bg:'rgba(34,211,238,0.12)', border:'rgba(34,211,238,0.28)'  },
  cancelled:  { color:'#F87171', bg:'rgba(248,113,113,0.12)', border:'rgba(248,113,113,0.28)' },
}[s?.toLowerCase()] || { color:'var(--nd-tx35)', bg:'var(--nd-w05)', border:'var(--nd-w12)' });

const StatusBadge = ({ status }) => {
  const c = statusCfg(status);
  return <span style={{ display:'inline-block',padding:'3px 9px',borderRadius:5,fontSize:10,fontWeight:700,letterSpacing:'0.06em',textTransform:'capitalize',fontFamily:T.mono,background:c.bg,color:c.color,border:`1px solid ${c.border}` }}>{status||'Unknown'}</span>;
};

const getRowClass = job => {
  const st = job.status?.toLowerCase();
  const sched = new Date(job.scheduled_date); sched.setHours(0,0,0,0);
  const today = new Date(); today.setHours(0,0,0,0);
  const nextWeek = new Date(); nextWeek.setDate(today.getDate()+7); nextWeek.setHours(0,0,0,0);
  if (st === 'completed') return 'jol-row-completed';
  if (sched.getTime() === today.getTime()) return 'jol-row-today';
  if (sched > today && sched <= nextWeek) return 'jol-row-week';
  if (sched < today && st !== 'completed') return 'jol-row-overdue';
  return '';
};

const FilterField = ({ label, icon: Icon, iconColor = T.muted, children }) => (
  <div style={{ minWidth:0 }}>
    <div style={{ fontSize:9,fontWeight:700,color:T.muted,letterSpacing:'0.12em',textTransform:'uppercase',marginBottom:6 }}>{label}</div>
    <div style={{ position:'relative' }}>
      {Icon && <Icon size={11} color={iconColor} style={{ position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',zIndex:1 }} />}
      {children}
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════
const JobOrdersPage = () => {
  const [jobOrders, setJobOrders]               = useState([]);
  const [status, setStatus]                     = useState('');
  const [type, setType]                         = useState('');
  const [city, setCity]                         = useState('');
  const [isLast, setIsLast]                     = useState(false);
  const [specificDate, setSpecificDate]         = useState('');
  const [page, setPage]                         = useState(1);
  const [limit]                                 = useState(10);
  const [total, setTotal]                       = useState(0);
  const [loading, setLoading]                   = useState(false);
  const [selectedJob, setSelectedJob]           = useState(null);
  const [showEditModal, setShowEditModal]       = useState(false);
  const [dateRange, setDateRange]               = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedJobOrder, setSelectedJobOrder] = useState(null);
  const [phone, setPhone]                       = useState('');
  const [debouncedPhone, setDebouncedPhone]     = useState('');

  const handleOpenCompleteModal  = job => { setSelectedJobOrder(job); setShowCompleteModal(true); };
  const handleCloseCompleteModal = ()  => { setShowCompleteModal(false); setSelectedJobOrder(null); };

  const fetchJobOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/job-orders/ops-job-orders`, {
        params: { status, type, city, isLast, page, limit, dateRange, specificDate, phone: debouncedPhone },
      });
      setJobOrders(response.data.rows || []);
      setTotal(response.data.total || 0);
    } catch (error) { console.error('Error fetching job orders:', error); }
    finally { setLoading(false); }
  }, [status, type, city, isLast, page, dateRange, specificDate, debouncedPhone, limit]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedPhone(phone), 500);
    return () => clearTimeout(t);
  }, [phone]);

  useEffect(() => { fetchJobOrders(); }, [fetchJobOrders]);

  const totalPages = Math.ceil(total / limit);

  const handleReset = () => {
    setStatus(''); setType('upcoming'); setCity(''); setIsLast(false);
    setPhone(''); setDateRange(''); setSpecificDate(''); setPage(1);
  };

  const maxVisible = 5;
  const startPage  = Math.max(1, page - Math.floor(maxVisible/2));
  const endPage    = Math.min(totalPages, startPage + maxVisible - 1);
  const adjStart   = Math.max(1, endPage - maxVisible + 1);

  return (
    <div style={{ background:T.bg, fontFamily:T.font, color:T.text, padding:'14px 20px' }}>
      <style>{STYLES}</style>

      {/* Page header */}
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:16, marginBottom:18, overflow:'hidden' }}>
        <div style={{ padding:'18px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:14 }}>
          <div>
            <div style={{ fontSize:9,color:T.muted,letterSpacing:'0.18em',marginBottom:5 }}>NOVAA · OPERATIONS</div>
            <div style={{ fontSize:26,fontWeight:800,color:'var(--nd-text-strong)',letterSpacing:'-0.03em',lineHeight:1 }}>Job Orders Management</div>
            <div style={{ fontSize:12,color:T.muted,marginTop:5 }}>Schedule, track and complete job orders</div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:9,color:T.muted,letterSpacing:'0.14em',textTransform:'uppercase',marginBottom:4 }}>Total Jobs</div>
            <div style={{ fontSize:36,fontWeight:900,color:T.teal,fontFamily:T.mono,lineHeight:1 }}>{total}</div>
          </div>
        </div>
        <div style={{ height:2,background:'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* Filters */}
      <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,marginBottom:16,overflow:'hidden' }}>
        <div style={{ padding:'12px 18px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:9 }}>
          <div style={{ width:26,height:26,borderRadius:7,background:'rgba(59,130,246,0.12)',border:'1px solid rgba(59,130,246,0.25)',display:'flex',alignItems:'center',justifyContent:'center' }}>
            <Filter size={13} color={T.blue} />
          </div>
          <span style={{ fontSize:13,fontWeight:700,color:T.text }}>Filters &amp; Search</span>
        </div>
        <div style={{ padding:'16px 18px' }}>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:12,marginBottom:14 }}>
            <FilterField label="Phone" icon={Phone} iconColor={T.teal}>
              <input className="jol-input" type="text" placeholder="Search by phone…" value={phone} onChange={e=>setPhone(e.target.value)} />
            </FilterField>
            <FilterField label="Specific Date" icon={Calendar} iconColor={T.blue}>
              <input className="jol-date" type="date" value={specificDate} onChange={e=>{ setSpecificDate(e.target.value); setDateRange(''); }} />
            </FilterField>
            <FilterField label="Status" icon={Filter} iconColor={T.purple}>
              <select className="jol-select" value={status} onChange={e=>setStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Completed">Completed</option>
                <option value="Dispatched">Dispatched</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </FilterField>
            <FilterField label="City" icon={MapPin} iconColor={T.amber}>
              <select className="jol-select" value={city} onChange={e=>setCity(e.target.value)}>
                <option value="">All Cities</option>
                <option value="Karachi">Karachi</option>
                <option value="Lahore">Lahore</option>
                <option value="Islamabad">Islamabad</option>
                <option value="Rawalpindi">Rawalpindi</option>
              </select>
            </FilterField>
            <FilterField label="Range Filter" icon={Calendar} iconColor={T.cyan}>
              <select className="jol-select" value={dateRange} onChange={e=>{ setDateRange(e.target.value); setSpecificDate(''); }}>
                <option value="">All Dates</option>
                <option value="today">Today</option>
                <option value="upcoming_week">Next 7 Days</option>
              </select>
            </FilterField>
          </div>
          <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10 }}>
            <label className="jol-check-wrap">
              <input type="checkbox" className="jol-checkbox" checked={isLast} onChange={e=>setIsLast(e.target.checked)} />
              <span style={{ fontSize:12,color:T.text,fontWeight:500 }}>Last Only</span>
            </label>
            <div style={{ display:'flex',gap:8 }}>
              <button className="jol-btn jol-btn-reset" onClick={handleReset}><X size={11} /> Reset</button>
              <button className="jol-btn jol-btn-refresh" onClick={fetchJobOrders}><RefreshCw size={11} /> Refresh</button>
            </div>
          </div>
        </div>
      </div>

      {/* Row legend */}
      <div style={{ display:'flex',gap:14,marginBottom:14,flexWrap:'wrap' }}>
        {[{ color:T.green,label:'Completed' },{ color:T.blue,label:"Today" },{ color:T.cyan,label:'Within 7 days' },{ color:T.red,label:'Overdue' }].map(({ color,label }) => (
          <div key={label} style={{ display:'flex',alignItems:'center',gap:5,fontSize:10,color:T.muted }}>
            <div style={{ width:8,height:8,borderRadius:2,background:color,opacity:0.7 }} />{label}
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:14,overflow:'hidden',marginBottom:16 }}>
        {loading ? (
          <div style={{ display:'flex',alignItems:'center',justifyContent:'center',padding:'52px 20px',gap:12 }}>
            <Loader size={20} color={T.blue} style={{ animation:'spin 0.8s linear infinite' }} />
            <span style={{ fontSize:13,color:T.muted }}>Loading job orders…</span>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%',borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  {['Customer','Contact','Location','Address','System','Status','Scheduled','Frequency','Actions'].map((h,i) => (
                    <th key={h} className="jol-th" style={i>=7?{textAlign:'center'}:{}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobOrders.length === 0 ? (
                  <tr><td colSpan={9} style={{ padding:'52px 20px',textAlign:'center',color:T.muted,fontSize:13 }}>
                    <div style={{ fontSize:42,marginBottom:12 }}>📋</div>
                    <div style={{ fontWeight:700,color:T.text,marginBottom:5 }}>No job orders found</div>
                    <div>Try adjusting your filters</div>
                  </td></tr>
                ) : (
                  jobOrders.map(job => (
                    <tr key={job.job_id} className={`jol-tr ${getRowClass(job)}`}>
                      <td className="jol-td"><span style={{ fontWeight:600,color:T.text,fontSize:12 }}>{job.customer_name||'N/A'}</span></td>
                      <td className="jol-td"><div style={{ display:'flex',alignItems:'center',gap:5,fontSize:11,color:T.muted,fontFamily:T.mono }}><Phone size={10} />{job.phone_number||'N/A'}</div></td>
                      <td className="jol-td" style={{ textAlign:'center' }}>
                        {job.pin_location ? (
                          <a href={job.pin_location} target="_blank" rel="noopener noreferrer" style={{ display:'inline-flex',alignItems:'center',justifyContent:'center',width:28,height:28,borderRadius:7,background:'rgba(59,130,246,0.12)',border:'1px solid rgba(59,130,246,0.25)',color:T.blue,textDecoration:'none' }} title="Open in Maps">
                            <MapPin size={13} />
                          </a>
                        ) : <span style={{ color:T.muted }}>—</span>}
                      </td>
                      <td className="jol-td" style={{ maxWidth:180 }}><div style={{ fontSize:11,color:T.muted,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:180 }}>{job.address||'N/A'}</div></td>
                      <td className="jol-td" style={{ textAlign:'center' }}><span style={{ background:'var(--nd-w08)',border:`1px solid ${T.border}`,color:T.muted,padding:'2px 8px',borderRadius:5,fontSize:10,fontWeight:700,fontFamily:T.mono }}>{job.system_size||'N/A'}</span></td>
                      <td className="jol-td" style={{ textAlign:'center' }}><StatusBadge status={job.status} /></td>
                      <td className="jol-td"><div style={{ display:'flex',alignItems:'center',gap:5,fontSize:11,color:T.text }}><Calendar size={10} color={T.muted} /><span style={{ fontFamily:T.mono }}>{new Date(job.scheduled_date).toLocaleDateString('en-GB')}</span></div></td>
                      <td className="jol-td" style={{ textAlign:'center' }}><span style={{ fontSize:11,color:T.muted }}>{job.frequency||'N/A'}</span></td>
                      <td className="jol-td" style={{ textAlign:'center' }}>
                        <div style={{ display:'flex',gap:6,justifyContent:'center' }}>
                          <button className="jol-btn jol-btn-complete" onClick={() => handleOpenCompleteModal(job)} title="Mark as Complete"><CheckCircle size={11} /></button>
                          <button className="jol-btn jol-btn-edit" onClick={() => { setSelectedJob(job); setShowEditModal(true); }} title="Edit Job"><Edit2 size={11} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && !loading && (
          <div style={{ padding:'12px 16px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10 }}>
            <div style={{ fontSize:11,color:T.muted,fontFamily:T.mono }}>
              Page <span style={{ color:T.text }}>{page}</span> of <span style={{ color:T.text }}>{totalPages}</span>
              <span style={{ marginLeft:8,color:'var(--nd-w20)' }}>({total} records)</span>
            </div>
            <div style={{ display:'flex',gap:5,alignItems:'center' }}>
              <button className="jol-page-btn" onClick={()=>setPage(1)} disabled={page===1}><ChevronsLeft size={12} /></button>
              <button className="jol-page-btn" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}><ChevronLeft size={12} /></button>
              {adjStart > 1 && <><button className="jol-page-btn" onClick={()=>setPage(1)}>1</button>{adjStart>2&&<span style={{ color:T.muted,fontSize:12,padding:'0 2px' }}><MoreHorizontal size={14} /></span>}</>}
              {Array.from({length:endPage-adjStart+1},(_,i)=>adjStart+i).map(p=>(
                <button key={p} className={`jol-page-btn${page===p?' active':''}`} onClick={()=>setPage(p)}>{p}</button>
              ))}
              {endPage < totalPages && <>{endPage<totalPages-1&&<span style={{ color:T.muted,fontSize:12,padding:'0 2px' }}><MoreHorizontal size={14} /></span>}<button className="jol-page-btn" onClick={()=>setPage(totalPages)}>{totalPages}</button></>}
              <button className="jol-page-btn" onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}><ChevronRight size={12} /></button>
              <button className="jol-page-btn" onClick={()=>setPage(totalPages)} disabled={page===totalPages}><ChevronsRight size={12} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCompleteModal && selectedJobOrder && (
        <CompleteJobOrderModal show={showCompleteModal} onHide={handleCloseCompleteModal} jobOrder={selectedJobOrder} onJobUpdated={fetchJobOrders} />
      )}
      <EditJobOrderModal show={showEditModal} onHide={()=>setShowEditModal(false)} jobOrder={selectedJob} onJobUpdated={fetchJobOrders} />
    </div>
  );
};

export default JobOrdersPage;